import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, onSnapshot, doc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DataProtectionNotice from '../components/DataProtectionNotice';
import { formatTimestamp } from '../utils/dateUtils';
import { runScheduleCheck, getScheduleIntervalMs } from '../utils/scheduleCheck';
import './ScoreboardPage.css';

const ScoreboardPage = () => {
  const { t } = useLanguage();
  const [activeCompetition, setActiveCompetition] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const previousActiveRoundIndexRef = useRef(null);

  useEffect(() => {
    let userUnsubscribe = null;
    let competitionUnsubscribe = null;

    const setup = async () => {
      await loadActiveCompetition();
    };

    setup();

    runScheduleCheck();
    const scheduleId = setInterval(runScheduleCheck, getScheduleIntervalMs());

    return () => {
      clearInterval(scheduleId);
      if (userUnsubscribe) userUnsubscribe();
      if (competitionUnsubscribe) competitionUnsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeCompetition?.id) {
      const userUnsub = setupRealtimeListener(activeCompetition.id);
      const compUnsub = setupCompetitionListener(activeCompetition.id);
      
      return () => {
        if (userUnsub) userUnsub();
        if (compUnsub) compUnsub();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompetition?.id]);

  const loadActiveCompetition = async () => {
    try {
      const competitionsRef = collection(db, 'competitions');
      const q = query(competitionsRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const competition = querySnapshot.docs[0].data();
        competition.id = querySnapshot.docs[0].id;
        setActiveCompetition(competition);
        loadUsers(competition.id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading competition:', err);
      setLoading(false);
    }
  };

  const setupRealtimeListener = (competitionId) => {
    const usersRef = collection(db, 'competitions', competitionId, 'users');
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      const usersList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => !user.frozen);
      
      calculateAndSortUsers(usersList);
    });

    return unsubscribe;
  };

  const setupCompetitionListener = (competitionId) => {
    const competitionRef = doc(db, 'competitions', competitionId);
    const unsubscribe = onSnapshot(competitionRef, (snapshot) => {
      if (snapshot.exists()) {
        const competition = { id: snapshot.id, ...snapshot.data() };
        
        // Detect round start/stop for animation
        if (competition.rounds) {
          const currentActiveRoundIndex = competition.rounds.findIndex(r => r.isActive);
          const previousActiveRoundIndex = previousActiveRoundIndexRef.current;
          
          // If round state changed (started or stopped), trigger animation
          if (currentActiveRoundIndex !== previousActiveRoundIndex) {
            // Round started or stopped
            setShowAnimation(true);
            setTimeout(() => {
              setShowAnimation(false);
            }, 3000); // Animation lasts 3 seconds
          }
          
          previousActiveRoundIndexRef.current = currentActiveRoundIndex;
        }
        
        setActiveCompetition(competition);
      }
    });

    return unsubscribe;
  };

  const loadUsers = async (competitionId) => {
    try {
      const usersRef = collection(db, 'competitions', competitionId, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const usersList = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => !user.frozen);
      
      calculateAndSortUsers(usersList);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateAndSortUsers = (usersList) => {
    const usersWithStats = usersList.map(user => {
      // Calculate average time per successful challenge from completed rounds
      // Sum of totalTimeUsedToSubmit for all rounds where isDone: true
      // Divided by sum of correctAnswersCount for all rounds where isDone: true
      let totalTimeUsedToSubmit = 0;
      let totalCorrectAnswersCount = 0;
      
      // Sum values from all completed rounds
      Object.values(user.roundData || {}).forEach(roundData => {
        if (roundData.isDone === true) {
          totalTimeUsedToSubmit += (roundData.totalTimeUsedToSubmit || 0);
          totalCorrectAnswersCount += (roundData.correctAnswersCount || 0);
        }
      });
      
      const averageTime = totalCorrectAnswersCount > 0 
        ? totalTimeUsedToSubmit / totalCorrectAnswersCount 
        : 0;

      return {
        ...user,
        averageTime
      };
    });

    // Sort: by score (desc), then average time (asc), then date of birth (youngest to oldest = most recent date first = desc)
    usersWithStats.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      if (a.averageTime !== b.averageTime) {
        return a.averageTime - b.averageTime;
      }
      // Sort by date of birth: youngest to oldest (most recent date first)
      const dateA = new Date(a.dateOfBirth);
      const dateB = new Date(b.dateOfBirth);
      return dateB - dateA; // Descending order (most recent first = youngest first)
    });

    setUsers(usersWithStats);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentRound = () => {
    if (!activeCompetition?.rounds) return 0;
    const activeRound = activeCompetition.rounds.findIndex(r => r.isActive);
    return activeRound >= 0 ? activeRound + 1 : 0;
  };

  // Build sorted user list for a specific round (only users who completed that round)
  const getUsersForRound = (roundIndex) => {
    const roundDataKey = String(roundIndex);
    return users
      .filter(user => {
        const rd = user.roundData?.[roundDataKey];
        return rd && rd.isDone === true;
      })
      .map(user => {
        const rd = user.roundData?.[roundDataKey] || {};
        const totalTime = rd.totalTimeUsedToSubmit || 0;
        const correctCount = rd.correctAnswersCount || 0;
        const roundAverageTime = correctCount > 0 ? totalTime / correctCount : 0;
        return {
          ...user,
          roundScore: rd.score || 0,
          roundAverageTime
        };
      })
      .sort((a, b) => {
        if (b.roundScore !== a.roundScore) return b.roundScore - a.roundScore;
        if (a.roundAverageTime !== b.roundAverageTime) return a.roundAverageTime - b.roundAverageTime;
        const dateA = new Date(a.dateOfBirth);
        const dateB = new Date(b.dateOfBirth);
        return dateB - dateA;
      });
  };

  const renderScoreTable = (tableUsers, getScore, getAverageTime) => (
    <table className="scoreboard-table">
      <thead>
        <tr>
          <th>#</th>
          <th>{t('name')}</th>
          <th>{t('totalScore')}</th>
          <th>{t('averageTimePerSuccessfulChallenge')}</th>
          <th>{t('dateOfBirth')}</th>
          <th>{t('gender')}</th>
        </tr>
      </thead>
      <tbody>
        {tableUsers.map((user, index) => (
          <tr key={user.id} className={index < 3 ? `rank-${index + 1}` : ''}>
            <td className="rank">{index + 1}</td>
            <td className="name">{user.name}</td>
            <td className="score">{getScore(user)}</td>
            <td className="time">{formatTime(getAverageTime(user))}</td>
            <td className="date-of-birth">****-**-**</td>
            <td className="gender">{user.gender ? t(user.gender) : '-'}</td>
          </tr>
        ))}
        {tableUsers.length === 0 && (
          <tr>
            <td colSpan="6" className="no-users">
              {t('noUsersYet')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  if (loading) {
    return (
      <div className="scoreboard-page">
        <Header />
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  // Generate colorful falling pieces
  const generateFallingPieces = () => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#a29bfe', '#00b894', '#00cec9'];
    const pieces = [];
    for (let i = 0; i < 50; i++) {
      const size = Math.random() * 20 + 10; // 10-30px
      const left = Math.random() * 100; // 0-100%
      const delay = Math.random() * 2; // 0-2s delay
      const duration = Math.random() * 2 + 3; // 3-5s duration
      const color = colors[Math.floor(Math.random() * colors.length)];
      const rotation = Math.random() * 360; // 0-360 degrees
      
      pieces.push({
        id: i,
        size,
        left,
        delay,
        duration,
        color,
        rotation
      });
    }
    return pieces;
  };

  const fallingPieces = showAnimation ? generateFallingPieces() : [];

  return (
    <div className="scoreboard-page">
      <Header />
      {showAnimation && (
        <div className="falling-pieces-container">
          {fallingPieces.map(piece => (
            <div
              key={piece.id}
              className="falling-piece"
              style={{
                left: `${piece.left}%`,
                width: `${piece.size}px`,
                height: `${piece.size}px`,
                backgroundColor: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`,
                '--initial-rotation': `${piece.rotation}deg`
              }}
            />
          ))}
        </div>
      )}
      <div className="scoreboard-container">
        <h1>{t('scoreboard')}</h1>
        
        {!activeCompetition ? (
          <div className="no-competition">
            <p>{t('noActiveCompetition')}</p>
          </div>
        ) : (
          <>
            <h2>{activeCompetition.name}</h2>
            <h3>
              {t('runningNow')} {t('round')} {getCurrentRound()} {t('outOf')} {activeCompetition.maxRounds || 0}
            </h3>
            {(activeCompetition.startTime || activeCompetition.endTime) && (
              <p className="scoreboard-period">
                {t('competitionPeriod')}: {formatTimestamp(activeCompetition.startTime) || t('manualControl')}
                {' – '}
                {formatTimestamp(activeCompetition.endTime) || t('manualControl')}
              </p>
            )}

            <h4 className="scoreboard-section-title">{t('globalScores')}</h4>
            <div className="table-container">
              {renderScoreTable(users, u => u.score, u => u.averageTime)}
            </div>

            {(activeCompetition.rounds || []).map((round, roundIndex) => {
              const roundUsers = getUsersForRound(roundIndex);
              const roundNumber = roundIndex + 1;
              const roundStart = formatTimestamp(round?.startTime);
              const roundEnd = formatTimestamp(round?.endTime);
              const showRoundPeriod = roundStart || roundEnd;
              return (
                <div key={roundIndex} className="scoreboard-round-section">
                  <h4 className="scoreboard-section-title">
                    {t('scoresForRound').replace('{number}', roundNumber)}
                  </h4>
                  {showRoundPeriod && (
                    <p className="scoreboard-period scoreboard-round-period">
                      {t('roundPeriod')}: {roundStart || t('manualControl')}
                      {' – '}
                      {roundEnd || t('manualControl')}
                    </p>
                  )}
                  <div className="table-container">
                    {renderScoreTable(
                      roundUsers,
                      u => u.roundScore,
                      u => u.roundAverageTime
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        <DataProtectionNotice />
      </div>
      <Footer />
    </div>
  );
};

export default ScoreboardPage;

